// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Carte, Erreur } from '../../composants/communs.tsx';
import type { ClientEncours } from './types.ts';

/**
 * Création ET modification d'un client — RG-096, RG-097.
 *
 * UN seul composant pour les deux gestes : les champs, les règles et les
 * messages sont identiques, et deux formulaires jumeaux divergeraient au
 * premier changement.
 *
 * Le client est GLOBAL : aucune boutique n'est demandée. C'est ce qui empêche
 * le contournement du plafond de crédit, qui consisterait à se faire une fiche
 * par boutique.
 */
export function FormulaireClient({
  client,
  onFerme,
}: {
  /** Absent : création. Présent : modification de cette fiche. */
  client?: ClientEncours;
  onFerme: () => void;
}): ReactNode {
  const { peut } = useSession();
  const cache = useQueryClient();
  const modification = client !== undefined;

  const [identite] = useState(() => cleNeuve());
  const [telephone, setTelephone] = useState(client?.telephone ?? '');
  const [nom, setNom] = useState(client?.nom ?? '');
  const [prenom, setPrenom] = useState(client?.prenom ?? '');
  const [repere, setRepere] = useState(client?.repere ?? '');
  // 100 000 F par défaut pour un nouveau client ; `null` = illimité.
  const [illimite, setIllimite] = useState(client !== undefined && client.plafond_credit === null);
  const [plafond, setPlafond] = useState(
    client === undefined || client.plafond_credit === null
      ? '100000'
      : String(client.plafond_credit),
  );
  const [delai, setDelai] = useState(
    client?.delai_credit_jours === null || client?.delai_credit_jours === undefined
      ? ''
      : String(client.delai_credit_jours),
  );

  // Le plafond n'est modifiable que par qui en détient la permission (RG-097).
  // Le champ est masqué aux autres — le serveur l'ignorerait de toute façon,
  // mais afficher un champ sans effet serait trompeur.
  const avecPlafond = peut('client.plafond.modifier');

  const complet = telephone.trim().length >= 8 && nom.trim().length >= 2;

  const envoi = useMutation({
    mutationFn: () => {
      const corps = {
        telephone: telephone.trim(),
        nom: nom.trim(),
        prenom: prenom.trim() === '' ? null : prenom.trim(),
        repere: repere.trim() === '' ? null : repere.trim(),
        ...(avecPlafond
          ? { plafond_credit: illimite ? null : Number(plafond === '' ? 0 : plafond) }
          : {}),
        delai_credit_jours: delai === '' ? null : Number(delai),
      };

      return modification
        ? appeler<{ id: string }>(`/clients/${client.id}`, {
            methode: 'PATCH',
            corps,
          })
        : appeler<{ id: string }>('/clients', {
            methode: 'POST',
            cleIdempotence: identite,
            corps,
          });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['clients'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre={modification ? `Modifier ${client.nom}` : 'Nouveau client'}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="client-telephone">Téléphone</label>
          <input
            id="client-telephone"
            inputMode="tel"
            maxLength={20}
            value={telephone}
            onChange={(evenement) => {
              setTelephone(evenement.target.value);
            }}
          />
          <small>
            Il identifie le client dans toutes les boutiques : deux fiches ne
            peuvent pas partager le même numéro.
          </small>
        </div>

        <div className="champ">
          <label htmlFor="client-nom">Nom</label>
          <input
            id="client-nom"
            maxLength={60}
            value={nom}
            onChange={(evenement) => {
              setNom(evenement.target.value);
            }}
          />
        </div>

        <div className="champ">
          <label htmlFor="client-prenom">Prénom (facultatif)</label>
          <input
            id="client-prenom"
            maxLength={60}
            value={prenom}
            onChange={(evenement) => {
              setPrenom(evenement.target.value);
            }}
          />
        </div>

        <div className="champ">
          <label htmlFor="client-repere">Repère (facultatif)</label>
          <input
            id="client-repere"
            maxLength={255}
            value={repere}
            onChange={(evenement) => {
              setRepere(evenement.target.value);
            }}
          />
          <small>
            « En face de la pharmacie du marché ». Sans repère, une créance
            devient irrécouvrable.
          </small>
        </div>

        {avecPlafond && (
          <div className="champ">
            <label htmlFor="client-plafond">Plafond de crédit (FCFA)</label>
            <input
              id="client-plafond"
              inputMode="numeric"
              value={illimite ? '' : plafond}
              placeholder={illimite ? 'Illimité' : undefined}
              disabled={illimite}
              onChange={(evenement) => {
                setPlafond(evenement.target.value.replace(/\D/g, ''));
              }}
            />
            <label style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                style={{ width: 'auto', marginRight: 8 }}
                checked={illimite}
                onChange={(evenement) => {
                  setIllimite(evenement.target.checked);
                }}
              />
              Crédit illimité
            </label>
            <small>
              Global à toutes les boutiques : il ne se multiplie pas en changeant
              de point de vente.
            </small>
          </div>
        )}

        <div className="champ">
          <label htmlFor="client-delai">Délai de crédit en jours (facultatif)</label>
          <input
            id="client-delai"
            inputMode="numeric"
            value={delai}
            onChange={(evenement) => {
              setDelai(evenement.target.value.replace(/\D/g, ''));
            }}
          />
          <small>À défaut, celui de la boutique s’applique.</small>
        </div>
      </div>

      <Erreur erreur={envoi.error} />
      <div className="ligne" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={!complet || envoi.isPending}
          onClick={() => {
            envoi.mutate();
          }}
        >
          {envoi.isPending
            ? 'Enregistrement…'
            : modification
              ? 'Enregistrer les modifications'
              : 'Créer le client'}
        </button>
      </div>
    </Carte>
  );
}
